import { EUserAccessPart, IConfig } from "@kuika/kuika-cl-model";
import React from "react";
import { connect } from "react-redux";
import { CloudService } from "../../../services/cloud-service";
import { IWithHistoryProp, withHistory } from "../../../shared/hoc/with-history";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import { setManagementLeftPanelCollapsed } from "../../../store/management-layout/actions";
import { AppState } from "../../../store/root-reducer";
import { setNextCallbackFunction } from "../../../store/unsaved-changes-modal/actions";
import { AcademyButton } from "../../design-tools/left-menu/academy-button";
import KuikaButton from "../../ui-kit/kuika-button/kuika-button";
import KuikaSelectionIndicator from "../../ui-kit/kuika-selection-indicator/kuika-selection-indicator";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import KuikaTooltip from "../../ui-kit/kuika-tooltip/kuika-tooltip";
import BoardNavBadge from "../board/board-nav-badge";

export interface IManagementLeftMenuState {}

/**
 * The active page's button. The academy button opens a menu over the page, so it keeps its own
 * active fill instead of the sliding one.
 */
const ACTIVE_PAGE_BUTTON_SELECTOR: string = ".kuika-button__active:not(.kuika-left-menu__academy-button)";

export interface IManagementLeftMenuProps {
  activeGlobalConfiguration?: IConfig;
  setNextCallbackFunction: (cb?: () => void) => void;
  isManagementLeftPanelCollapsed: boolean;
  setManagementLeftPanelCollapsed: (value: boolean) => void;
}

class ManagementLeftMenu extends React.PureComponent<
  IManagementLeftMenuProps & IWithHistoryProp,
  IManagementLeftMenuState
> {
  constructor(props) {
    super(props);
    this.state = {};
  }

  handleRecentClick = () => {
    const redirectToRecent = () => {
      this.props.navigator.gotoRecent();
    };
    if (this.props.activeGlobalConfiguration) {
      this.props.setNextCallbackFunction(redirectToRecent);
      return;
    }

    redirectToRecent();
  };

  handleSettingsClick = () => {
    const redirect = () => {
      const hasUserAccessSubscription = KMainFunctions.hasUserAccessInWorkspace(EUserAccessPart.EditSubscription);
      const hasUserAccessEditUser = KMainFunctions.hasUserAccessInWorkspace(EUserAccessPart.AddEditUser);
      if (hasUserAccessEditUser) {
        this.props.navigator.gotoUsers();
      } else if (hasUserAccessSubscription) {
        this.props.navigator.gotoSubsciptions();
      }
    };

    if (this.props.activeGlobalConfiguration) {
      this.props.setNextCallbackFunction(redirect);
      return;
    }

    redirect();
  };

  render() {
    const hasAccessEditUser = KMainFunctions.hasUserAccessInWorkspace(EUserAccessPart.AddEditUser);
    const hasAccessSubscription = KMainFunctions.hasUserAccessInWorkspace(EUserAccessPart.SubscriptionManagement);
    const hasAccessManageConfiguration = KMainFunctions.hasUserAccessInWorkspace(EUserAccessPart.ManageConfigurations);

    let pathname = document.location.pathname.substring(1);
    if (pathname.endsWith("/")) {
      pathname = pathname.slice(0, -1);
    }
    const parts = pathname.split(/\//);
    const url = [...parts].pop()?.toLowerCase();
    return (
      <div className="kuika-left-menu">
        {/* The brand mark moved to the shell header (ManagementTopBar) and re-opening a
            collapsed panel is the edge tab's job (withManagement), so the strip is
            navigation only now — same anatomy as the designer's left menu. */}
        <div className="kuika-left-menu__menu-items-wrapper">
          <KuikaSelectionIndicator activeSelector={ACTIVE_PAGE_BUTTON_SELECTOR} continuityKey="management-left-menu" />
          <div className="kuika-left-menu__menu-items">
            <KuikaTooltip content="Apps" position="right">
              <KuikaButton
                data-testid="appsButton"
                data-onboarding-id="management_left-menu_apps"
                type="tertiary"
                size="md"
                isIcon
                color="neutral"
                active={
                  url === "projects" ||
                  url === "allprojects" ||
                  url === "recent" ||
                  url === "myprojects" ||
                  url === "favorites" ||
                  url === "archive"
                }
                onClick={() => this.handleRecentClick()}
              >
                <KuikaSvg name="apps" width={18} height={18} pointer />
              </KuikaButton>
            </KuikaTooltip>
            {hasAccessManageConfiguration && (
              <KuikaTooltip content="Global Configurations" position="right">
                <KuikaButton
                  data-testid="globalConfigurationsButton"
                  data-onboarding-id="management_left-menu_global-configurations"
                  type="tertiary"
                  size="md"
                  isIcon
                  color="neutral"
                  active={url === "globalconfigurations"}
                  onClick={() => this.props.navigator.goToGlobalConfig()}
                >
                  <KuikaSvg type="sidebar-icons" name="device-desktop-cog" width={18} height={18} pointer />
                </KuikaButton>
              </KuikaTooltip>
            )}
            {CloudService.GetIsCloudEnabled() && (
              <KuikaTooltip content="Cloud" position="right">
                <KuikaButton
                  data-testid="cloudButton"
                  type="tertiary"
                  size="md"
                  isIcon
                  color="neutral"
                  active={parts[1] === "cloud" || url === "cloud"}
                  onClick={() => this.props.navigator.gotoCloud()}
                >
                  <KuikaSvg type="datasource-icons" name="cloud-cog" width={18} height={18} pointer />
                </KuikaButton>
              </KuikaTooltip>
            )}
            {(hasAccessEditUser || hasAccessSubscription) && (
              <KuikaTooltip content="Settings" position="right">
                <KuikaButton
                  data-testid="settingsButton"
                  data-onboarding-id="management_left-menu_settings"
                  type="tertiary"
                  size="md"
                  isIcon
                  color="neutral"
                  active={
                    url === "settings" ||
                    url === "subscriptions" ||
                    url === "componentset" ||
                    url === "preferences" ||
                    url === "users" ||
                    url === "logs" ||
                    url === "roles" ||
                    url === "componentlist" ||
                    parts[2] === "componentlist" ||
                    parts[2] === "subscriptionPlans" ||
                    url === "onboarding"
                  }
                  onClick={() => this.handleSettingsClick()}
                >
                  <KuikaSvg type="sidebar-icons" name="settings" width={18} height={18} pointer />
                </KuikaButton>
              </KuikaTooltip>
            )}
          </div>
          {/* Bottom cluster, mirroring the designer strip: Board, help and documentation are
              product-wide, and Apps is where a new user lands — needing to open a project
              first just to find help was the gap. Board sits here rather than in the top
              nav group because it is a cross-project destination, not a view of the app
              list. The wrapper's space-between pins this group to the bottom. Audit and
              the shortcut sheet stay designer-only. */}
          <div className="kuika-left-menu__menu-items">
            <KuikaTooltip content="Board" position="right">
              <KuikaButton
                data-testid="boardButton"
                type="tertiary"
                size="md"
                isIcon
                color="neutral"
                active={url === "board" || parts[1] === "board" || parts[1] === "issue"}
                onClick={() => this.props.navigator.gotoBoard()}
                style={{ position: "relative" }}
              >
                <KuikaSvg type="sidebar-icons" name="board" width={18} height={18} pointer />
                <BoardNavBadge />
              </KuikaButton>
            </KuikaTooltip>
            {/*
              Helpdesk is not offered here. The panel reads and writes the screen-design
              context — tickets, selection, the half-written ticket — and the management shell
              is mounted outside that provider, so every action resolved to the partition
              defaults: the modal opened onto an empty state whose Create button did nothing.
              The tool lives in the designer shell, which has the provider.
            */}
            <AcademyButton />
          </div>
        </div>
      </div>
    );
  }
}
const mapStateToProps = (state: AppState) => ({
  activeGlobalConfiguration: state.globalConfig.activeGlobalConfiguration,
  isManagementLeftPanelCollapsed: state.managementLayout.isLeftPanelCollapsed
});
const mapDispatchToProps = (dispatch) => ({
  setNextCallbackFunction: (cb?: () => void) => dispatch(setNextCallbackFunction(cb)),
  setManagementLeftPanelCollapsed: (value: boolean) => dispatch(setManagementLeftPanelCollapsed(value))
});

const managementLeftMenu = connect(
  mapStateToProps,
  mapDispatchToProps
)(withHistory<IManagementLeftMenuProps>(ManagementLeftMenu));
export { managementLeftMenu as ManagementLeftMenu };
